/**
 * Where cards sit on a printed sheet, and - the whole point of this file -
 * how the answer side has to be arranged so it lines up once the paper is
 * turned over.
 *
 * Shared by the flash cards and the card games (ADR 0135). It is generic over
 * what a card holds, so each feature keeps its own card type.
 *
 * Nothing here imports @react-pdf/renderer. The arrangement is the part that is
 * easy to get wrong and invisible until someone has printed, cut, and glued a
 * set with every answer on the wrong card, so it is plain data in and plain
 * data out, and it is tested.
 */

/** Cards across and down a US Letter sheet. */
export const CARD_COLUMNS = 2;
export const CARD_ROWS = 4;
export const CARDS_PER_SHEET = CARD_COLUMNS * CARD_ROWS;

/** 3.5in x 2.5in at 72pt/in - a standard index card, and it tiles Letter exactly. */
export const CARD_WIDTH = 252;
export const CARD_HEIGHT = 180;

/** Letter is 612x792, so the grid centres with these margins and no rounding. */
export const SHEET_PADDING_X = (612 - CARD_COLUMNS * CARD_WIDTH) / 2;
export const SHEET_PADDING_Y = (792 - CARD_ROWS * CARD_HEIGHT) / 2;

/** A grid slot: a card, or a gap where a sheet ran out of cards. */
export type CardSlot<T> = T | null;

/**
 * The cards of one sheet, in rows, with any short final row padded out to full
 * width.
 *
 * The padding is not cosmetic. A trailing gap has to occupy a real slot so that
 * mirroring the row moves the last card to the correct column instead of
 * sliding it along - see backRows.
 */
export function frontRows<T>(cards: T[]): CardSlot<T>[][] {
    const rows: CardSlot<T>[][] = [];

    for (let start = 0; start < cards.length; start += CARD_COLUMNS) {
        const row: CardSlot<T>[] = cards.slice(start, start + CARD_COLUMNS);
        while (row.length < CARD_COLUMNS) {
            row.push(null);
        }
        rows.push(row);
    }

    return rows;
}

/**
 * The same sheet arranged for the back of the paper.
 *
 * A duplex printer flipping on the long edge turns a portrait sheet about its
 * vertical centre line, so the leftmost column on the front comes out rightmost
 * on the back. Each row is therefore reversed, while the order of the rows down
 * the page is left alone. Print the answer side without this and every answer
 * is behind its neighbour rather than its own problem.
 */
export function backRows<T>(cards: T[]): CardSlot<T>[][] {
    return frontRows(cards).map((row) => [...row].reverse());
}

/** Splits a deck into sheets of `CARDS_PER_SHEET`; the last may be short. */
export function sheetsOf<T>(cards: T[]): T[][] {
    const sheets: T[][] = [];
    for (let start = 0; start < cards.length; start += CARDS_PER_SHEET) {
        sheets.push(cards.slice(start, start + CARDS_PER_SHEET));
    }
    return sheets;
}
